<script setup>
/* eslint-env jquery */
import { computed, nextTick, onMounted, ref } from 'vue';
import repositoryGeneric from '../../../data/repositoryGeneric';
import ModalInputField from '../../utils/ModalInputField.vue';
import CreateOrderByImportAddresses from './CreateOrderByImportAddresses.vue';
import CreateOrderByImportLocations from './CreateOrderByImportLocations.vue';
import { handleResponseWithMessages } from '../../../helpers/wwwHelper';
import { createToaster } from '@meforma/vue-toaster';
import {
  MODAL_BOROUGHS_TABLE,
  MODAL_COUNTIES_TABLE,
  MODAL_DIRECTIONS_TABLE,
  MODAL_ORDER_INITIATIVES_TABLE,
  MODAL_REGIONS_TABLE,
  MODAL_USERS_TABLE
} from '../../../data/constants/modalConstants';
import DebugPanel from '../../test/DebugPanel.vue';
import ModalTable from '@/components/utils/ModalTable.vue';
import { isNotEmptyArray } from '../../../helpers/utilsHelper';
import { TOASTER_SERVER_ERROR_MSG } from '../../../data/constants/toasterMessagesConstants';
import { useRouter } from 'vue-router';
import FormErrorPanel from '../../utils/FormErrorPanel.vue';

const props = defineProps({
  attachment5: null,
  attachment6: null
});

const router = useRouter();
const dataObject = ref(null);
const isLoading = ref(false);
const modalTable = ref();
const formErrors = ref([]);
const displayName = ref({
  direction: null,
  county: null,
  borough: null,
  region: null,
  coordinator: null,
  initiative: null
});
const formRef = ref();
const toaster = createToaster({ position: 'top-right', duration: 3000 });

const dataReady = computed(() => {
  return (
    dataObject.value?.attachment_5 != null &&
    Array.isArray(dataObject.value?.record_points) &&
    Array.isArray(dataObject.value?.no_record_points)
  );
});

const directionInvalid = computed(() => {
  return dataObject.value?.attachment_5.direction_object == null;
});

const countyInvalid = computed(() => {
  return dataObject.value?.attachment_5.county_object == null;
});

const boroughInvalid = computed(() => {
  return dataObject.value?.attachment_5.commune_object == null;
});

const regionInvalid = computed(() => {
  return dataObject.value?.attachment_5.region_object == null;
});

async function fetchData() {
  isLoading.value = true;

  const formData = new FormData();
  formData.append('attachment_5', props.attachment5);
  formData.append('attachment_6', props.attachment6);

  const response = await repositoryGeneric.fetchPostAndQueryParamsWithFormData(
    `order/readImportFiles`,
    null,
    formData
  );

  dataObject.value = {};

  handleResponseWithMessages(response, null, () => {
    dataObject.value = response;
    initializeAfterFetch();
  });

  isLoading.value = false;
}

async function initializeAfterFetch() {
  dataObject.value.attachment_5.coordinator_object = null;
  dataObject.value.attachment_5.order_initiative = null;

  displayName.value.direction = dataObject.value?.attachment_5.direction_object?.name ?? null;
  displayName.value.county = dataObject.value?.attachment_5.county_object?.name ?? null;
  displayName.value.borough = dataObject.value?.attachment_5.commune_object?.name ?? null;
  displayName.value.region = dataObject.value?.attachment_5.region_object?.name ?? null;

  let locationId = 0;

  if (isNotEmptyArray(dataObject.value.record_points)) {
    dataObject.value.record_points.forEach((location) => {
      location.id = locationId++;
    });
  }

  if (isNotEmptyArray(dataObject.value.no_record_points)) {
    dataObject.value.no_record_points.forEach((location) => {
      location.id = locationId++;
    });
  }

  await nextTick();

  initializeDatepickers();

  $('#incomingDate .input-group.date').datepicker(
    'setDate',
    dataObject.value.attachment_5?.incoming_date
  );

  $('#deadline .input-group.date').datepicker('setDate', dataObject.value.attachment_5?.deadline);
}

async function submitForm() {
  isLoading.value = true;

  createGroupsArray();

  const response = await repositoryGeneric.fetchPostAndQueryParams(
    `order/createCompleteOrder`,
    null,
    dataObject.value
  );

  if (response?.serverError) {
    toaster.show(TOASTER_SERVER_ERROR_MSG, { type: 'error' });
  } else {
    if (response) {
      if (response.id != null) {
        toaster.show('Zlecenie zostało utworzone.', { type: 'success' });
        router.push({ name: 'orderDetails', params: { id: response.id } });
      } else formErrors.value = response;
    }
  }

  isLoading.value = false;
}

function createGroupsArray() {
  dataObject.value.groups_array = [];

  if (!isNotEmptyArray(dataObject.value.no_record_points)) return;

  const groupedLocations = dataObject.value.no_record_points.filter(
    (location) => location.id_group != null
  );

  if (!isNotEmptyArray(groupedLocations)) return;

  const groups = groupedLocations.reduce((acc, location) => {
    acc[location.id_group] = acc[location.id_group] || [];
    acc[location.id_group].push(location);
    return acc;
  }, {});

  dataObject.value.groups_array = Object.values(groups).map((group) => {
    const firstElement = group[0];
    return {
      id: firstElement.id_group,
      sz_geo: firstElement.sz_geo,
      dl_geo: firstElement.dl_geo,
      count: group.length,
      id_dem_elem_type: firstElement.id_dem_elem_type,
      note: firstElement.note
    };
  });
}

function onModalCompleted(data) {
  modalTable.value.hide();

  switch (modalTable.value.modal.type) {
    case MODAL_DIRECTIONS_TABLE:
      dataObject.value.attachment_5.direction_object = data ?? null;
      displayName.value.direction = data?.name ?? '';
      break;
    case MODAL_COUNTIES_TABLE:
      dataObject.value.attachment_5.county_object = data ?? null;
      displayName.value.county = data?.name ?? '';
      break;
    case MODAL_BOROUGHS_TABLE:
      dataObject.value.attachment_5.commune_object = data ?? null;
      displayName.value.borough = data?.name ?? '';
      break;
    case MODAL_REGIONS_TABLE:
      dataObject.value.attachment_5.region_object = data ?? null;
      displayName.value.region = data?.name ?? '';
      break;
    case MODAL_USERS_TABLE:
      dataObject.value.attachment_5.coordinator_object = data ?? null;
      displayName.value.coordinator = (data?.firstName ?? '') + ' ' + (data?.lastName ?? '');
      break;
    case MODAL_ORDER_INITIATIVES_TABLE:
      dataObject.value.attachment_5.order_initiative = data ?? null;
      displayName.value.initiative = data?.name ?? '';
      break;
  }
}

function onModalRejected() {
  modalTable.value.hide();

  switch (modalTable.value.modal.type) {
    case MODAL_DIRECTIONS_TABLE:
      dataObject.value.attachment_5.direction_object = null;
      displayName.value.direction = null;
      break;
    case MODAL_COUNTIES_TABLE:
      dataObject.value.attachment_5.county_object = null;
      displayName.value.county = null;
      break;
    case MODAL_BOROUGHS_TABLE:
      dataObject.value.attachment_5.commune_object = null;
      displayName.value.borough = null;
      break;
    case MODAL_REGIONS_TABLE:
      dataObject.value.attachment_5.region_object = null;
      displayName.value.region = null;
      break;
    case MODAL_USERS_TABLE:
      dataObject.value.attachment_5.coordinator_object = null;
      displayName.value.coordinator = null;
      break;
    case MODAL_ORDER_INITIATIVES_TABLE:
      dataObject.value.attachment_5.order_initiative = null;
      displayName.value.initiative = null;
      break;
  }
}

function initializeDatepickers() {
  const datepickerConfig = {
    todayBtn: 'linked',
    keyboardNavigation: true,
    forceParse: true,
    calendarWeeks: true,
    autoclose: true,
    weekStart: 1
  };

  $('#incomingDate .input-group.date').datepicker(datepickerConfig);
  $(document.body).on('change', 'input[id^="incomingDateId"]', updateIncomingDateFromPicker);

  $('#deadline .input-group.date').datepicker(datepickerConfig);
  $(document.body).on('change', 'input[id^="deadlineId"]', updateDeadlineFromPicker);
}

function updateIncomingDateFromPicker() {
  dataObject.value.attachment_5.incoming_date = document.getElementById('incomingDateId').value;
}

function updateDeadlineFromPicker() {
  dataObject.value.attachment_5.deadline = document.getElementById('deadlineId').value;
}

function requestFormSubmit() {
  if (formRef.value?.reportValidity()) {
    submitForm();
  }
}

onMounted(() => {
  fetchData();
});

defineExpose({ requestFormSubmit });
</script>

<template>
  <FormErrorPanel :errors="formErrors" />

  <div class="ibox">
    <div
      class="ibox-content p-0"
      :class="{ 'sk-loading': isLoading }"
      style="border-style: none; min-height: 200px"
    >
      <div class="sk-spinner sk-spinner-three-bounce">
        <div class="sk-bounce1"></div>
        <div class="sk-bounce2"></div>
        <div class="sk-bounce3"></div>
      </div>

      <form v-if="dataReady" ref="formRef" role="form" @submit.prevent="submitForm" class="mt-4">
        <div class="row">
          <div class="col-xl-6">
            <div class="row">
              <div class="col-xl-6">
                <div class="form-group">
                  <label>Numer zlecenia</label>
                  <input
                    type="text"
                    placeholder="Wpisz numer zlecenia"
                    v-model="dataObject.attachment_5.order_number"
                    class="form-control"
                    minlength="1"
                    maxlength="100"
                    required
                  />
                </div>

                <div class="form-group" id="incomingDate">
                  <label>Data wpływu zlecenia</label>
                  <div class="input-group date">
                    <span class="input-group-addon" :style="'background-color: #e9ecef'">
                      <i class="fa fa-calendar"></i>
                    </span>
                    <input
                      id="incomingDateId"
                      type="text"
                      class="form-control"
                      v-model="dataObject.attachment_5.incoming_date"
                      required
                    />
                  </div>
                </div>

                <ModalInputField
                  :style="directionInvalid ? 'margin-bottom: 0;' : ''"
                  title="Rodzaj zlecenia"
                  :displayText="displayName.direction"
                  nullText="Wybierz rodzaj zlecenia"
                  :required="true"
                  :selectStyle="directionInvalid ? 'color: red;' : ''"
                  @showModal="modalTable.showModal(MODAL_DIRECTIONS_TABLE)"
                />
                <div v-if="directionInvalid" class="mb-2 text-danger">
                  Niepoprawna nazwa rodzaju zlecenia: "{{ dataObject.attachment_5.direction }}"
                </div>

                <div class="form-group" id="deadline">
                  <label>Termin realizacji</label>
                  <div class="input-group date">
                    <span class="input-group-addon" :style="'background-color: #e9ecef'">
                      <i class="fa fa-calendar"></i>
                    </span>
                    <input
                      id="deadlineId"
                      type="text"
                      class="form-control"
                      v-model="dataObject.attachment_5.deadline"
                      required
                    />
                  </div>
                </div>

                <ModalInputField
                  title="Koordynator"
                  :displayText="displayName.coordinator"
                  nullText="Wybierz koordynatora"
                  :required="true"
                  @showModal="
                    modalTable.showModal(MODAL_USERS_TABLE, {
                      additionalProps: {
                        filter: { url: 'user/coordinators' }
                      }
                    })
                  "
                />
              </div>

              <div class="col-xl-6">
                <div class="form-group">
                  <label>Województwo</label>
                  <input
                    type="text"
                    placeholder="Wpisz województwo"
                    v-model="dataObject.attachment_5.voivodeship"
                    class="form-control"
                    minlength="1"
                    maxlength="100"
                    required
                  />
                </div>

                <ModalInputField
                  :style="countyInvalid ? 'margin-bottom: 0;' : ''"
                  title="Powiat"
                  :displayText="displayName.county"
                  nullText="Wybierz powiat"
                  :required="true"
                  :selectStyle="countyInvalid ? 'color: red;' : ''"
                  @showModal="modalTable.showModal(MODAL_COUNTIES_TABLE)"
                />
                <div v-if="countyInvalid" class="mb-2 text-danger">
                  Niepoprawna nazwa powiatu: "{{ dataObject.attachment_5.county }}"
                </div>

                <ModalInputField
                  :style="boroughInvalid ? 'margin-bottom: 0;' : ''"
                  title="Gmina"
                  :displayText="displayName.borough"
                  nullText="Wybierz gminę"
                  :required="true"
                  :selectStyle="boroughInvalid ? 'color: red;' : ''"
                  @showModal="modalTable.showModal(MODAL_BOROUGHS_TABLE)"
                />
                <div v-if="boroughInvalid" class="mb-2 text-danger">
                  Niepoprawna nazwa gminy: "{{ dataObject.attachment_5.commune }}"
                </div>

                <ModalInputField
                  :style="regionInvalid ? 'margin-bottom: 0;' : ''"
                  title="Region"
                  :displayText="displayName.region"
                  nullText="Wybierz region"
                  :required="true"
                  :selectStyle="regionInvalid ? 'color: red;' : ''"
                  @showModal="modalTable.showModal(MODAL_REGIONS_TABLE)"
                />
                <div v-if="regionInvalid" class="mb-2 text-danger">
                  Niepoprawna nazwa regionu: "{{ dataObject.attachment_5.region }}"
                </div>

                <ModalInputField
                  title="Inicjatywa"
                  :displayText="displayName.initiative"
                  nullText="Wybierz inicjatywę"
                  :required="false"
                  @showModal="modalTable.showModal(MODAL_ORDER_INITIATIVES_TABLE)"
                />
              </div>
            </div>

            <CreateOrderByImportAddresses v-model:addresses="dataObject.attachment_5.addresses" />
          </div>

          <div class="col-xl-6">
            <CreateOrderByImportLocations
              v-model:recordPoints="dataObject.record_points"
              v-model:noRecordPoints="dataObject.no_record_points"
            />
          </div>
        </div>
      </form>
    </div>
  </div>

  <ModalTable
    ref="modalTable"
    @onModalCompleted="onModalCompleted"
    @onModalRejected="onModalRejected"
  ></ModalTable>

  <DebugPanel>
    <pre>{{ dataObject }}</pre>
  </DebugPanel>
</template>

<style scoped></style>
